import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = 
  [
    "input",
    "previewContainer",
    "previewImage",
    "buttonText" , 
    "previewIcon"
  ]
  static values = {
    accept: { type: String, default: "*/*" }
  }

  static outlets = ["dropzone"]

  connect() {
    this._ensureMultipartForm()
    this._setupGlobalListeners()
    this._defaultButtonText = this.hasButtonTextTarget ? this.buttonTextTarget.innerText : ""
  }

  disconnect() {
    this._teardownGlobalListeners()
  }

  //   MODAL CONTROLS (Single Responsibility: Visibility) 

  openModal(event) {
    if (event) event.preventDefault()
    this._toggleModal(true)
    window.activeMediaPicker = this 
    this.loadAssets(this.acceptValue)
    // Broadcast this so Dropzone controller knows too
    window.dispatchEvent(new CustomEvent("media-picker:opened", { 
      detail: { acceptType: this.acceptValue } 
    }))
  }

  closeModal() {
    this._toggleModal(false)
    window.activeMediaPicker = null
  }

 async selectAsset(asset, clickedRowElement = null) {
    // Prevent double-clicks
    if (this.isProcessingAsset) return 

    if (asset.is_video) {
      this._syncExistingAsset(asset)
      this.closeModal()
      return
    }

    try {
      this.isProcessingAsset = true // Lock the UI

      // Show loading state on the row
      let originalHtml = ""
      if (clickedRowElement) {
        originalHtml = clickedRowElement.innerHTML
        clickedRowElement.classList.add("bg-blue-50", "pointer-events-none")
        clickedRowElement.innerHTML = `<div class="col-span-12 text-center py-2 text-sm font-bold text-blue-600 animate-pulse">Downloading for cropper...</div>`
      }

      if (!this.hasDropzoneOutlet) {
        throw new Error("Dropzone outlet is not connected! Check HTML data attributes.")
      }

      const response = await fetch(asset.url)
      if (!response.ok) throw new Error("Failed to download image from server.")
      const blob = await response.blob()
      
      const file = new File([blob], asset.name || "library_asset.jpg", { type: blob.type })
      
      // Restore the clicked row's HTML
      if (clickedRowElement) {
        clickedRowElement.innerHTML = originalHtml
        clickedRowElement.classList.remove("bg-blue-50", "pointer-events-none")
      }

      // Pass it to Dropzone directly via the outlet
      this.dropzoneOutlet.dropzone.addFile(file)
      
      // Scroll to the cropper using the outlet's HTML element
      this.dropzoneOutlet.element.scrollIntoView({ behavior: "smooth", block: "start" })
      this.dropzoneOutlet.notyf.success("Image imported! Please select a crop format.")

    } catch (error) {
      console.error(" CROPPER IMPORT FAILED:", error)
      this._syncExistingAsset(asset)
      this.closeModal()
    } finally {
      this.isProcessingAsset = false // Unlock the UI
    }
  }
  // Drops whatever is staged in this picker and puts the preview back to its
  // empty state. Nothing is posted, so the record keeps the image it has until
  // the admin picks a different one.
  clear(event) {
    if (event) event.preventDefault()

    this.inputTarget.value = ""
    this.inputTarget.disabled = false

    const rawInput = this.element.querySelector(".media-picker-raw-file")
    if (rawInput) rawInput.remove()

    if (this.hasPreviewContainerTarget) {
      if (this.hasPreviewImageTarget) {
        this.previewImageTarget.src = ""
        this.previewImageTarget.classList.add("hidden")
      }
      if (this.hasPreviewIconTarget) this.previewIconTarget.classList.add("hidden")
      this.previewContainerTarget.classList.add("hidden")
    }

    if (this.hasButtonTextTarget) this.buttonTextTarget.innerText = this._defaultButtonText
  }

  selectRawFile(file) {
    const objectUrl = URL.createObjectURL(file)
    this._syncNewFile(file, objectUrl)
    this.closeModal()
  }

  // THE WORKERS (Private-ish helper methods starting with _) 

  _syncExistingAsset(asset) {
    this.inputTarget.value = asset.id
    this.inputTarget.disabled = false
    this._disableRawFileInput()
    this._updatePreviewUI({
          url: asset.url,
          thumb_url: asset.thumb_url,
          is_video: asset.is_video
    })
}

  _syncNewFile(file, url) {
    this.inputTarget.value = ""
    this.inputTarget.disabled = true
    
    const rawInput = this._getOrCreateRawFileInput()
    this._injectFileIntoInput(file, rawInput)
    
    this._updatePreviewUI({
          url: url,
          thumb_url: file.dataURL,
          is_video: file.type.startsWith("video/")
    })
}
_updatePreviewUI(data) {
    if (this.hasPreviewContainerTarget) {
    
      //  If we have a thumbnail OR it's a regular image
      if (data.thumb_url || !data.is_video) {
        this.previewImageTarget.src = data.thumb_url || data.url;
        this.previewImageTarget.classList.remove("hidden");
        this.previewIconTarget.classList.add("hidden");
      } 
      //  Fallback: It's a video but NO thumbnail exists yet
      else {
        this.previewImageTarget.classList.add("hidden");
        this.previewIconTarget.classList.remove("hidden");
      }

      this.previewContainerTarget.classList.remove("hidden");
    }
      
    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.innerText = data.is_video ? "Change Video" : "Change Image";
    }
}

  // --- DATA FETCHING & RENDERING ---

  async loadAssets(acceptType ) {
    const container = document.getElementById("media-list-container")
    if (!container) return

    this._renderLoading(container)

    try {
      let url = "/admin/assets.json"
      if (acceptType && acceptType !== "*/*") {
        url += `?accept=${encodeURIComponent(acceptType)}`
      }

      const response = await fetch(url)
      const assets = await response.json()
      this._renderAssetList(container, assets)
    } catch (e) {
      this._renderError(container)
    }
  }

  _renderAssetList(container, assets) {
    container.innerHTML = ""
    if (assets.length === 0) {
      container.innerHTML = `<div class="p-10 text-center text-gray-400">Library is empty.</div>`
      return
    }
    assets.forEach(asset => container.appendChild(this._createAssetRow(asset)))
  }

  _createAssetRow(asset) {
    const row = document.createElement("div")
    row.className = "grid grid-cols-12 gap-4 items-center px-6 py-3 border-b border-gray-100 hover:bg-blue-50 cursor-pointer group last:border-b-0"
    
    let previewHtml = '';
    
    if (asset.thumb_url) {
      // Best case: We have a thumbnail (works for both images and videos)
      previewHtml = `<img src="${asset.thumb_url}" class="max-h-full max-w-full object-contain">`;
      
    } else if (asset.is_video) {
      // Fallback: It's a video but no thumb yet. Show a nice video icon instead of a broken image.
      previewHtml = `
        <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>`;
        
    } else {
      // Fallback: It's an image but no thumb yet. Just use the original image.
      previewHtml = `<img src="${asset.url}" class="max-h-full max-w-full object-contain">`;
    }

    row.innerHTML = `
      <div class="col-span-1">
        <div class="h-10 w-10 bg-white rounded border border-gray-200 overflow-hidden flex items-center justify-center">
          ${previewHtml}
        </div>
      </div>
      <div class="col-span-8">
        <p class="text-sm font-semibold text-gray-800 truncate">${asset.name || 'Unnamed Asset'}</p>
      </div>
      <div class="col-span-3 text-right">
        <span class="text-xs font-bold text-blue-600 opacity-0 group-hover:opacity-100 uppercase tracking-widest transition-opacity">Select File →</span>
      </div>
    `
    row.onclick = (event) => this.selectAsset(asset, event.currentTarget)
    return row
  }

  // DOM & BROWSER UTILITIES 

  _injectFileIntoInput(file, input) {
    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    input.files = dataTransfer.files // replace the entire files of the input with new one
    input.disabled = false
  }

  _getOrCreateRawFileInput() {
    let fileInput = this.element.querySelector('input[type="file"].media-picker-raw-file')
    if (!fileInput) {
      fileInput = document.createElement('input')
      fileInput.type = 'file'
      fileInput.className = 'hidden media-picker-raw-file'
      fileInput.name = this.inputTarget.name 
      this.element.appendChild(fileInput)
    }
    return fileInput
  }

  _disableRawFileInput() {
    const fileInput = this.element.querySelector('.media-picker-raw-file')
    if (fileInput) fileInput.disabled = true
  }

  _toggleModal(visible) {
    const modal = document.getElementById("media-picker-modal")
    if (!modal) return
    modal.classList.toggle("hidden", !visible)
    document.body.classList.toggle("overflow-hidden", visible)
  }

  _setupGlobalListeners() {
    this.boundCloseModal = this.closeModal.bind(this)
    this.boundKeydown = (e) => { if (e.key === "Escape") this.closeModal() }
    
    document.addEventListener("keydown", this.boundKeydown)
    const closeBtn = document.getElementById("close-media-modal")
    if (closeBtn) closeBtn.addEventListener("click", this.boundCloseModal)
  }

  _teardownGlobalListeners() {
    document.removeEventListener("keydown", this.boundKeydown)
    const closeBtn = document.getElementById("close-media-modal")
    if (closeBtn) closeBtn.removeEventListener("click", this.boundCloseModal)
  }

  _ensureMultipartForm() {
    const form = this.element.closest("form")
    if (form) form.setAttribute("enctype", "multipart/form-data")
  }

  _renderLoading(el) { el.innerHTML = `<div class="p-10 text-center text-blue-500 animate-pulse font-medium">Connecting...</div>` }
  _renderError(el) { el.innerHTML = `<div class="p-10 text-center text-red-500 font-bold">Error loading assets</div>` }
}